/**
 * Content Manager Service
 * Handles API requests to Strapi Content Manager endpoints (for admin users)
 * These endpoints require admin authentication tokens
 */

import { API_CONFIG, getContentManagerUrl } from '../config/api.config';
import { storageService } from '../utils/storage';
import { performRenewToken } from './auth-permissions';
import type { ContentManagerQueryOptions, ContentManagerResponse } from './content-manager.types';
import {
  buildContentManagerQueryParams,
  parseContentManagerError,
} from './content-manager.helpers';

// Re-export types for backward compatibility
export type { ContentManagerQueryOptions, ContentManagerResponse } from './content-manager.types';

class ContentManagerService {
  /**
   * Build headers with admin authentication token
   */
  private buildHeaders(): HeadersInit {
    const token = storageService.getToken();

    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Fetch wrapper that renews the session once on a 401 (expired access
   * cookie) and retries the request with fresh headers, mirroring
   * api.service's renewal flow.
   */
  private async fetchWithRenewal(
    url: string,
    init: RequestInit,
    isRetry = false
  ): Promise<Response> {
    const response = await fetch(url, init);
    if (response.status !== 401 || isRetry) {
      return response;
    }
    try {
      await performRenewToken();
    } catch {
      // Renewal failed — surface the original 401 to the caller
      return response;
    }
    return this.fetchWithRenewal(url, { ...init, headers: this.buildHeaders() }, true);
  }

  /**
   * Get all items from a Content Manager collection
   */
  async getAll<T>(
    collectionType: string,
    options?: ContentManagerQueryOptions
  ): Promise<ContentManagerResponse<T>> {
    try {
      const url = getContentManagerUrl(collectionType);
      const queryString = buildContentManagerQueryParams(options);
      const fullUrl = `${url}${queryString}`;

      console.log('📡 [Content Manager] GET:', fullUrl);

      const response = await this.fetchWithRenewal(fullUrl, {
        method: 'GET',
        headers: this.buildHeaders(),
      });

      if (!response.ok) {
        await parseContentManagerError(response, 'Failed to fetch data');
      }

      const data = await response.json();
      console.log('✅ [Content Manager] Response:', data);

      return data;
    } catch (error: any) {
      console.error('❌ [Content Manager] Request failed:', error);
      throw error;
    }
  }

  /**
   * Get a single item by ID from a Content Manager collection
   */
  async getById<T>(
    collectionType: string,
    id: number | string,
    options?: Pick<ContentManagerQueryOptions, 'populate'>
  ): Promise<T> {
    try {
      const url = getContentManagerUrl(collectionType);
      const queryString = options?.populate
        ? buildContentManagerQueryParams({ populate: options.populate })
        : '';
      const fullUrl = `${url}/${id}${queryString}`;

      console.log('📡 [Content Manager] GET by ID:', fullUrl);

      const response = await this.fetchWithRenewal(fullUrl, {
        method: 'GET',
        headers: this.buildHeaders(),
      });

      if (!response.ok) {
        await parseContentManagerError(response, 'Failed to fetch item');
      }

      const data = await response.json();
      console.log('✅ [Content Manager] Response:', data);

      return data;
    } catch (error: any) {
      console.error('❌ [Content Manager] Request failed:', error);
      throw error;
    }
  }

  /**
   * Get a single-type entry — single types live under /single-types/{uid},
   * not /collection-types/{uid}/…, so collection helpers 404 on them.
   */
  async getSingle<T>(model: string): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}/content-manager/single-types/${model}`;
    const response = await this.fetchWithRenewal(url, {
      method: 'GET',
      headers: this.buildHeaders(),
    });
    if (!response.ok) {
      await parseContentManagerError(response, 'Failed to fetch item');
    }
    return response.json();
  }

  /**
   * Upsert a single-type entry — PUT /single-types/{uid} creates or updates.
   */
  async updateSingle<T>(model: string, data: Partial<T>): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}/content-manager/single-types/${model}`;
    const requestBody: any = { ...data };
    delete requestBody.documentId;
    const response = await this.fetchWithRenewal(url, {
      method: 'PUT',
      headers: this.buildHeaders(),
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      await parseContentManagerError(response, 'Failed to update item');
    }
    return response.json();
  }

  /**
   * Create a new item in a Content Manager collection
   */
  async create<T>(collectionType: string, data: Partial<T>): Promise<T> {
    try {
      const url = getContentManagerUrl(collectionType);

      console.log('📡 [Content Manager] POST:', url);
      console.log('📦 [Content Manager] Input data:', data);

      const requestBody = data;
      console.log('📤 [Content Manager] Request body:', requestBody);

      const response = await this.fetchWithRenewal(url, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        await parseContentManagerError(response, 'Failed to create item');
      }

      const result = await response.json();
      console.log('✅ [Content Manager] Created:', result);

      return result;
    } catch (error: any) {
      console.error('❌ [Content Manager] Request failed:', error);
      throw error;
    }
  }

  /**
   * Update an item in a Content Manager collection
   * @param collectionType - The Strapi collection type (e.g., 'api::client.client')
   * @param documentId - The Strapi documentId (NOT numeric id)
   * @param data - The data to update
   */
  async update<T>(collectionType: string, documentId: string, data: Partial<T>): Promise<T> {
    try {
      const url = `${getContentManagerUrl(collectionType)}/${documentId}`;

      console.log('📡 [Content Manager] PUT:', url);
      console.log('📦 [Content Manager] Input data:', data);

      const requestBody: any = { ...data };
      delete requestBody.documentId;

      console.log('📤 [Content Manager] Request body:', requestBody);

      const response = await this.fetchWithRenewal(url, {
        method: 'PUT',
        headers: this.buildHeaders(),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        await parseContentManagerError(response, 'Failed to update item');
      }

      const result = await response.json();
      console.log('✅ [Content Manager] Updated:', result);

      return result;
    } catch (error: any) {
      console.error('❌ [Content Manager] Request failed:', error);
      throw error;
    }
  }

  /**
   * Delete an item from a Content Manager collection
   * @param collectionType - The Strapi collection type
   * @param documentId - The Strapi documentId (NOT numeric id)
   */
  async delete(collectionType: string, documentId: string): Promise<void> {
    try {
      const url = `${getContentManagerUrl(collectionType)}/${documentId}`;

      console.log('📡 [Content Manager] DELETE:', url);

      const response = await this.fetchWithRenewal(url, {
        method: 'DELETE',
        headers: this.buildHeaders(),
      });

      if (!response.ok) {
        await parseContentManagerError(response, 'Failed to delete item');
      }

      console.log('✅ [Content Manager] Deleted successfully');
    } catch (error: any) {
      console.error('❌ [Content Manager] Request failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const contentManagerService = new ContentManagerService();
export default contentManagerService;
